import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, tokens } from "../lib/api";
import { registerNativePush, unregisterNativePush } from "../lib/pushNative";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const hydrate = useCallback(async () => {
    if (!tokens.access) {
      setLoading(false);
      return;
    }
    /* BUG-014 — 2 routes authentifiées sur 17 finissaient sur /login, sur
       Firefox uniquement, avec `GET /auth/me :: NS_BINDING_ABORTED` dans la
       même capture. Mesuré le 20/09 sur 340 chargements authentifiés Firefox :
       1 redirection, soit 0,29 % — rare, mais reel.

       Le code ci-dessous etait deja prudent : une requête ANNULEE n'a pas de
       réponse, donc pas de 401, donc la session n'était pas vidée. C'est
       l'étage d'après qui trahissait : `loading` passait quand même à `false`
       avec `user` jamais posé, et la garde de route, voyant « pas
       d'utilisateur », renvoyait vers /login un utilisateur parfaitement
       connecté.

       Un échec SANS RÉPONSE ne dit rien sur la session — surtout pas qu'elle
       est finie. Tant que le jeton est là, on redemande une fois avant de
       conclure. Un 401/403, lui, reste definitif : c'est une reponse. */
    const demander = async () => {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setCompany(data.company);
    };

    try {
      await demander();
    } catch (e) {
      // Only clear the session on explicit auth failures (401/403).
      // 404, 5xx, network errors must NOT log the user out.
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        tokens.clear();
        setUser(null);
        setCompany(null);
      } else if (tokens.access) {
        try {
          await demander();
        } catch (_e2) {
          /* Deux tentatives sans reponse exploitable : on rend la main. La
             session n'est PAS videe — le jeton reste, et le prochain rendu ou
             la prochaine navigation retentera. */
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  // Enregistre le token push natif dès qu'un utilisateur est authentifié
  // (no-op sur le web). Upsert idempotent : sans effet de bord si déjà fait.
  useEffect(() => {
    if (user) registerNativePush();
  }, [user]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    tokens.set(data.access_token, data.refresh_token);
    setUser(data.user);
    setCompany(data.company);
    return data;
  }, []);

  const register = useCallback(async (payload) => {
    // Double opt-in : pas de connexion auto, l'utilisateur confirme par email.
    const { data } = await api.post("/auth/register", payload);
    return data; // { message, email }
  }, []);

  const verifyEmail = useCallback(async (token) => {
    const { data } = await api.post("/auth/verify-email", { token });
    tokens.set(data.access_token, data.refresh_token);
    setUser(data.user);
    setCompany(data.company);
    return data;
  }, []);

  const resendVerification = useCallback(async (email) => {
    return (await api.post("/auth/resend-verification", { email })).data;
  }, []);

  const logout = useCallback(async () => {
    // Supprime le token push natif tant que la session est encore valide.
    await unregisterNativePush();
    try {
      await api.post("/auth/logout");
    } catch (_e) {
      /* no-op */
    }
    tokens.clear();
    setUser(null);
    setCompany(null);
  }, []);

  const refreshAuth = useCallback(async () => {
    await hydrate();
  }, [hydrate]);

  const value = { user, company, loading, login, register, verifyEmail, resendVerification, logout, refreshAuth };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
